import { all, call, put, select, takeLatest } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert } from "../Alerts/actions";
import { fetchWoundReportSuccess, fetchWoundReportFailure } from "./actions";
import { FETCH_WOUND_REPORT } from "./constants";

// `from`/`to` are optional — the API defaults to the last 12 whole months and
// snaps both ends to month boundaries (a half-month at either end of a trend
// line reads as a fall in incidence).
export const buildWoundReportQuery = ({ from, to } = {}) => {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

/**
 * The report is a single read. `takeLatest` (not `takeEvery`) because the period
 * filter re-fires on every change and a slow earlier window must never land on
 * top of a newer one — an inspector reading last quarter's numbers under this
 * quarter's heading is worse than a spinner.
 */
export function* fetchWoundReportSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, from, to } = payload || {};
  try {
    const query = buildWoundReportQuery({ from, to });
    const { data } = yield call(
      api(token).get,
      `/api/location/${locationId}/wound-report${query}`,
    );
    yield put(fetchWoundReportSuccess(data || null));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    // 409 = the wound-projection migration has not run on this environment. It
    // is a known, explained state and the API says so in the message — surface
    // the reason on the page rather than a red toast the manager cannot act on.
    if (e?.response?.status === 409) {
      yield put(
        fetchWoundReportFailure(
          typeof e?.error === "string"
            ? e.error
            : "The wound register is not available on this environment yet.",
        ),
      );
      return;
    }
    yield put(fetchWoundReportFailure(null));
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while fetching the wound report!"));
  }
}

export default function* WoundReportSaga() {
  yield all([takeLatest(FETCH_WOUND_REPORT, fetchWoundReportSaga)]);
}
